import { Box, Button, Paper, Stack, Typography, Chip } from "@mui/material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";

import NavTabs from "../../../../../components/Tabs/NavTabs";
import { SpecTab } from "./StructureModelingTabs/SpecTab";
import { EstimationTab } from "./StructureModelingTabs/EstimationTab";
import { PartLib } from "./StructureModelingTabs/PartLibTab";
import { ModelingTab } from "./StructureModelingTabs/ModelingTab";
import { TablesTab } from "../../../AllEquipments/CommonFeatures/SidePanel/TablesTab/TablesTable";

import {
  StructureModelingProvider,
  useStructureModeling,
} from "./structureModelingContext";

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { EstimationRevisionSelector } from "../Modeling/StructureModelingTabs/EstimationTab/EstimationRevisionSelector";
import { ModelingControls } from "../../../AllEquipments/CommonFeatures/SidePanel/ModelingControls";
import { SpecHeader } from "../Modeling/StructureModelingTabs/SpecTab/SpecHeader";

import Validation from "../../Piping/MainTabs/Validation";
import CompareRevisionsButton from "../../../AllEquipments/CommonFeatures/ElectronButtons/CompareRevisionsButton";

// IMPORTANT:
// Use a structural version of this component if your Piping one depends on usePiping().
// Do NOT import the Piping combo if it internally calls usePiping().
import LoadProjectDataComboBox from "./LoadProjectDataComboBox";

const structureProjects = [
  { id: "5", name: "Estimation BOM" },
  { id: "6", name: "3D BOM" },
  { id: "7", name: "2D BOM" },
];

function ProjectSelectionHeader({
  plantId,
  clientName,
  type,
  selectedProject,
  onProjectSelect,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        px: 1.5,
        py: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 210 }}
        >
          <FolderOpenRoundedIcon fontSize="small" />

          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ whiteSpace: "nowrap" }}
          >
            Load Project Data
          </Typography>
        </Stack>

        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <LoadProjectDataComboBox
            plantId={plantId}
            clientName={clientName}
            type={type}
            label="Select Project"
            onSelect={onProjectSelect}
          />
        </Box>

        {selectedProject && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={selectedProject?.name || selectedProject?.link || "Selected"}
            sx={{
              maxWidth: 220,
              fontWeight: 600,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        )}
      </Stack>
    </Paper>
  );
}

function ModelingTabs() {
  const {
    saveStructuralData,
    modelingData,
    setModelingData,
    selectedModelingRevision,
    loadStructuralData,
  } = useStructureModeling();

  const { id: projectId, plantId } = useParams();

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);

  const compareBtnRef = useRef(null);

  const clientName = "DEMO";
  const type = "struc";

  const tabs = [
    { label: "Spec", panel: <SpecTab /> },
    { label: "Spec Library", panel: <PartLib /> },
    { label: "Estimation", panel: <EstimationTab /> },
    // { label: "Modeling", panel: <ModelingTab /> },
    { label: "Tables", panel: <TablesTab tab="structural" /> },
    {
      label: "Validation",
      panel: (
        <Validation
          useGivenContext={useStructureModeling}
          projects={structureProjects}
        />
      ),
    },
  ];

  function handleSave() {
    saveStructuralData();
  }

  function handleChange(newValue) {
    console.log("Tab changed to:", newValue);
    setCurrentTab(newValue);
  }

  async function handleProjectSelect(project) {
    console.log("selected structural project", project);

    setSelectedProject(project);

    const selectedId = project?.id || project?.projectId || project?.value;

    if (!selectedId) {
      console.warn("Selected project does not have id/projectId/value:", project);
      return;
    }

    await loadStructuralData(selectedId);
  }

  useEffect(() => {
    window.__menuHandlers = window.__menuHandlers || {};

    window.__menuHandlers.COMPARE = () => {
      compareBtnRef.current?.triggerOpen();
    };

    return () => {
      if (window.__menuHandlers) {
        window.__menuHandlers.COMPARE = null;
      }
    };
  }, [modelingData, setModelingData, selectedModelingRevision]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flex: 1,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <ProjectSelectionHeader
          plantId={plantId || projectId}
          clientName={clientName}
          type={type}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
        />

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <NavTabs
            tabs={tabs}
            navLabel="mcs-tab"
            handleOutputChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {currentTab === 0 && (
              <Box sx={{ flexGrow: 1 }}>
                <SpecHeader />
              </Box>
            )}

            {currentTab === 2 && (
              <Box sx={{ flexGrow: 1 }}>
                <EstimationRevisionSelector />
              </Box>
            )}

            {currentTab === 3 && (
              <Box sx={{ flexGrow: 1 }}>
                <ModelingControls />
              </Box>
            )}

            <Box display="flex">
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Box>

            <Box display="flex" sx={{ p: 1 }}>
              <CompareRevisionsButton
                ref={compareBtnRef}
                modelingData={modelingData}
                setModelingData={setModelingData}
                revKey={selectedModelingRevision}
              />
            </Box>
          </NavTabs>
        </Paper>
      </Box>

      {/* {currentTab === 3 && (
        <Box>
          <SidePanel />
        </Box>
      )} */}
    </Box>
  );
}

export function Modeling() {
  const { projectId } = useParams();

  return (
    <StructureModelingProvider id={projectId}>
      <ModelingTabs />
    </StructureModelingProvider>
  );
}